import React, { useState, useCallback, useContext, useEffect, useRef, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Avatar } from "@material-ui/core";
import { Warning, Today, Schedule, Chat, ErrorOutline, Announcement, AssignmentInd } from "@material-ui/icons";
import TicketsManager from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import taskService from "../../services/taskService";
import { getBackendUrl } from "../../config";
import logo from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";
import useSettings from "../../hooks/useSettings";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { parseISO, isPast, isToday, isTomorrow, format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useDate } from "../../hooks/useDate";

const defaultTicketsManagerWidth = 550;
const minTicketsManagerWidth = 430;
const maxTicketsManagerWidth = 700;

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		height: `calc(100% - 48px)`,
		[theme.breakpoints.down('sm')]: {
			height: '100%'
		},
		overflowY: "hidden",
		background: theme.palette.tabHeaderBackground,
	},
	chatPapper: {
		display: "flex",
		height: "100%",
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		padding: "14px",
		flexDirection: "column",
		overflowY: "hidden",
		position: "relative",
		border: "none",
			},
	messagesWrapper: {
		display: "flex",
		padding: "14px",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
		border : "none",
	},
	welcomeMsg: {
		background: theme.palette.tabHeaderBackground,
		display: "flex",
		justifyContent: "center", // centraliza o conteúdo verticalmente (com apenas 1 filho)
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		borderRadius: 12,
		border: "None",
		userSelect: "none",
	},
	dragger: {
		width: "2px",
		cursor: "ew-resize",
		padding: "0px",
		position: "absolute",
		top: "50%",
		transform: "translateY(-50%)",
		right: 0,
		height: "30%",
		bottom: 0,
		zIndex: 100,
		backgroundColor: "#d1d1d1",
		userSelect: "none", // Evita a seleção de texto no elemento de redimensionamento
		opacity: 0.7,
		borderRadius: 12,
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
	},
	welcomeDashboard: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center", // alinha horizontalmente todo o conteúdo
		justifyContent: "center", // centraliza verticalmente o bloco (enquanto couber)
		width: "100%",
		height: "100%",
		padding: theme.spacing(3),
		backgroundColor: theme.palette.tabHeaderBackground,
	},
	dashboardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: theme.spacing(3),
		marginTop: theme.spacing(2), // reduz distância do topo em telas maiores
		[theme.breakpoints.down('sm')]: {
			marginTop: 0, // remove afastamento do topo em telas pequenas
		},
	},
	logoContainer: {
		marginRight: theme.spacing(2),
		display: "flex",
		alignItems: "center",
	},
	logoImage: {
		height: "60px",
		width: "auto",
		objectFit: "contain",
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
	},
	headerContent: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	systemName: {
		fontSize: "1.6rem",
		fontWeight: 600,
		color: theme.palette.primary.main,
		lineHeight: 1.2,
	},
	welcomeSection: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(3),
	},
	welcomeContent: {
		display: "flex",
		alignItems: "flex-start", // Alinha no topo para melhor controle
		justifyContent: "center",
		gap: theme.spacing(2),
	},
	welcomeText: {
		textAlign: "center",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center", // Centraliza o texto verticalmente
	},
	avatar2: {
		width: "50px",
		height: "50px",
		cursor: "pointer",
		borderRadius: "50%",
		border: "2px solid #ccc",
		flexShrink: 0,
		marginTop: "8px", // Ajuste fino para alinhar com o nome
	},
	greeting: {
		fontSize: "1.1rem",
		fontWeight: 500,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(0.5),
	},
	datetime: {
		fontSize: "1rem",
		color: theme.palette.text.secondary,
		marginBottom: 0, // Remove margin bottom para alinhamento
	},
	summaryText: {
		fontSize: "1rem",
		color: theme.palette.text.primary,
		fontWeight: 500,
		textAlign: "center",
		marginBottom: theme.spacing(3),
	},
	userInfo: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.3),
	},
	expirationSection: {
		textAlign: "center",
		marginBottom: theme.spacing(3),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
	},
	expirationCard: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		backgroundColor: theme.palette.warning.main,
		padding: theme.spacing(1.5, 2),
		borderRadius: 8,
		border: `1px solid ${theme.palette.warning.dark}`,
		width: "fit-content",
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
	},
	pendingAnnouncementsCard: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		backgroundColor: theme.palette.info.main,
		padding: theme.spacing(1.5, 2),
		borderRadius: 8,
		border: `1px solid ${theme.palette.info.dark}`,
		width: "fit-content",
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
	},
	expirationText: {
		fontSize: "0.95rem",
		color: "#fff",
		fontWeight: 500,
		whiteSpace: "nowrap",
	},
	expirationIcon: {
		color: "#fff",
		fontSize: "1.2rem",
	},
	statsGrid: {
		width: "100%",
		flex: 1,
		// Centralização e agrupamento
		maxWidth: 960,
		margin: "0 auto",
		justifyContent: "center",
		// manter comportamento vertical original (sem centralizar verticalmente)
		alignItems: "flex-start",
		alignContent: "flex-start", // evita esticar linhas e criar espaços grandes entre elas
		// responsividade do bloco para telas muito grandes ou médias
		[theme.breakpoints.up('xl')]: {
			maxWidth: 1100,
		},
		[theme.breakpoints.down('md')]: {
			maxWidth: '100%',
		},
		"& .MuiGrid-item": {
			display: "flex",
			alignItems: "flex-start",
			marginBottom: "0 !important",
		},
		"& .MuiGrid-container": {
			alignItems: "flex-start",
			"& > .MuiGrid-item": {
				paddingTop: "4px !important",
				paddingBottom: "4px !important",
			},
		},
	},
	statCard: {
		borderRadius: 12,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		transition: "all 0.2s ease-in-out",
		height: "fit-content",
		width: "100%",
		// Limita a largura máxima dos cards e centraliza em telas médias e acima
		[theme.breakpoints.up('md')]: {
			maxWidth: 400,
			marginLeft: 'auto',
			marginRight: 'auto',
		},
	},
	statCardContent: {
		padding: `${theme.spacing(1.25)}px !important`,
		textAlign: "center",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "flex-start",
		gap: theme.spacing(0.75),
	},
	statHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
	},
	statLabel: {
		fontSize: "0.8rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		lineHeight: 1.1,
		textAlign: "center",
		wordBreak: "break-word",
		hyphens: "auto",
	},
	statFooter: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(0.8),
		width: "100%",
	},
	statNumber: {
		fontSize: "1.6rem",
		fontWeight: 700,
		lineHeight: 1,
	},
	statIcon: {
		fontSize: "1.6rem",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	overdueCard: {
		"& $statNumber": { color: "#f44336" },
		"& $statLabel": { color: "#f44336" },
		"& $statIcon": { color: "#f44336" },
	},
	slaWarnCard: {
		"& $statNumber": { color: "#ff9800" },
		"& $statLabel": { color: "#ff9800" },
		"& $statIcon": { color: "#ff9800" },
	},
	slaOverdueCard: {
		"& $statNumber": { color: "#f44336" },
		"& $statLabel": { color: "#f44336" },
		"& $statIcon": { color: "#f44336" },
	},
	todayCard: {
		"& $statNumber": { color: "#4caf50" },
		"& $statLabel": { color: "#4caf50" },
		"& $statIcon": { color: "#4caf50" },
	},
	tomorrowCard: {
		"& $statNumber": { color: "#2196f3" },
		"& $statLabel": { color: "#2196f3" },
		"& $statIcon": { color: "#2196f3" },
	},
	ticketsCard: {
		"& $statNumber": { color: "#9c27b0" },
		"& $statLabel": { color: "#9c27b0" },
		"& $statIcon": { color: "#9c27b0" },
	},
}));

const TicketsCustom = () => {
	const { user, socket } = useContext(AuthContext);
	const { settings } = useSettings();
	const { get: getCompanySetting } = useCompanySettings();
	const { dateToClient } = useDate();

	const classes = useStyles({ ticketsManagerWidth: user.defaultTicketsManagerWidth || defaultTicketsManagerWidth });

	const { ticketId } = useParams();

	// Estados para o dashboard
	const [tasksStats, setTasksStats] = useState({ overdue: 3, today: 5, tomorrow: 2 }); // Valores temporários para teste
	const [openTicketsCount, setOpenTicketsCount] = useState(8); // Exibido no card "Atendimentos Abertos"
	const [assignedOpenTicketsCount, setAssignedOpenTicketsCount] = useState(0); // Novo card: oficiais atribuídos ao usuário
	const [sessionOpenTicketsCount, setSessionOpenTicketsCount] = useState(0); // Contagem de sessões atribuídas ao usuário
	const [currentDateTime, setCurrentDateTime] = useState(new Date());
	const [isLoading, setIsLoading] = useState(true);
	const [profileUrl, setProfileUrl] = useState(null);
	const [pendingAnnouncementsCount, setPendingAnnouncementsCount] = useState(0);
	const [slaWarnAssignedCount, setSlaWarnAssignedCount] = useState(0);
	const [slaOverdueAssignedCount, setSlaOverdueAssignedCount] = useState(0);

	const backendUrl = getBackendUrl();
	// Flags Kanban Avançado (alinhar com Kanban/index.js): plano + setting manualTicketCreation
	const normalizeBool = (v) => v === true || v === 1 || v === "true" || v === "1";
	const initialPlanAdv = normalizeBool(
		[user?.company?.plan?.useKanbanAdvanced, user?.plan?.useKanbanAdvanced, user?.useKanbanAdvanced]
			.find(v => v !== undefined && v !== null)
	);
	const [planAdvanced, setPlanAdvanced] = useState(initialPlanAdv);
	const [manualTicketCreationEnabled, setManualTicketCreationEnabled] = useState(false);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const advancedTicketsEnabled = useMemo(
		() => Boolean(planAdvanced) && (settingsLoaded ? Boolean(manualTicketCreationEnabled) : false),
		[planAdvanced, manualTicketCreationEnabled, settingsLoaded]
	);

	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(0);
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);

	useEffect(() => {
		if (user && user.defaultTicketsManagerWidth) {
			setTicketsManagerWidth(user.defaultTicketsManagerWidth);
		}
	}, [user]);

	// Carregar company setting relevante (manualTicketCreation)
	useEffect(() => {
		let mounted = true;
		const fetchSetting = async () => {
			try {
				if (!user?.companyId) { setSettingsLoaded(true); return; }
				const setting = await getCompanySetting({ column: "manualTicketCreation" });
				if (!mounted) return;
				setManualTicketCreationEnabled(String(setting?.manualTicketCreation || "disabled") === "enabled");
				setSettingsLoaded(true);
			} catch (e) {
				if (!mounted) return;
				setManualTicketCreationEnabled(false);
				setSettingsLoaded(true);
			}
		};
		fetchSetting();
		return () => { mounted = false; };
	}, [user?.companyId, getCompanySetting]);

	// Assinar atualizações em tempo real de settings e plano (igual Kanban)
	useEffect(() => {
		const companyId = user?.companyId;
		if (!companyId || !socket) return;

		const onCompanySettings = (data) => {
			if (data?.action !== "update") return;
			const key = data?.setting?.key;
			const value = data?.setting?.value;
			if (key === "manualTicketCreation") {
				setManualTicketCreationEnabled(value === "enabled");
				setSettingsLoaded(true);
			}
		};

		const onPlan = (data) => {
			if (data?.action !== "update") return;
			const useAdv = Boolean(data?.plan?.useKanbanAdvanced);
			setPlanAdvanced(useAdv);
		};

		const chSettings = `company-${companyId}-companySettings`;
		const chPlan = `company-${companyId}-plan`;
		socket.on(chSettings, onCompanySettings);
		socket.on(chPlan, onPlan);

		return () => {
			socket.off(chSettings, onCompanySettings);
			socket.off(chPlan, onPlan);
		};
	}, [socket, user?.companyId]);

	// Configurar URL do avatar do usuário
	useEffect(() => {
		if (user && user.companyId) {
			const ImageUrl = user.profileImage;
			if (ImageUrl !== undefined && ImageUrl !== null) {
				setProfileUrl(`${backendUrl}/public/company${user.companyId}/user/${ImageUrl}`);
			} else {
				setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);
			}
		}
	}, [user, backendUrl]);

	// Função para buscar estatísticas das tarefas
	const fetchTasksStats = useCallback(async () => {
		try {
			const response = await taskService.getAll({ companyId: user.companyId });
			
			// Considerar apenas tarefas abertas (não concluídas) e não arquivadas
			const rawTasks = response.tasks || response.data || response || [];
			const tasks = rawTasks.filter(t => (t?.status || "pending") !== "completed" && !t?.isArchived);
			
			const now = new Date();
			const stats = {
				overdue: 0,
				today: 0,
				tomorrow: 0,
			};

			tasks.forEach(task => {
				if (task.dueDate) {
					const dueDate = parseISO(task.dueDate);
					if (isPast(dueDate) && !isToday(dueDate)) {
						stats.overdue++;
					} else if (isToday(dueDate)) {
						stats.today++;
					} else if (isTomorrow(dueDate)) {
						stats.tomorrow++;
					}
				}
			});

			setTasksStats(stats);
		} catch (error) {
			// Definir valores padrão em caso de erro
			setTasksStats({ overdue: 0, today: 0, tomorrow: 0 });
		}
	}, [user.companyId]);

	// Função para buscar informativos pendentes
	const fetchPendingAnnouncements = useCallback(async () => {
		try {
			
			// Usar o endpoint correto que existe: /announcement-views/unread-count
			const response = await api.get("/announcement-views/unread-count");
			
			
			// O endpoint retorna { count: number }
			const pendingCount = response.data?.count || 0;
						
			setPendingAnnouncementsCount(pendingCount);
		} catch (error) {
			console.error("Erro ao buscar informativos pendentes:", error);
			console.error("Status:", error.response?.status);
			console.error("Data:", error.response?.data);
			
			// Em caso de erro, definir como 0
			setPendingAnnouncementsCount(0);
		}
	}, [user.id]);

	// Função para buscar contagem de tickets abertos do usuário
	const fetchOpenTicketsCount = useCallback(async () => {
		try {
			
			// Usar o mesmo endpoint que o MomentsUser usa
			const response = await api.get("/usersMoments");
			
			// Filtrar tickets que são do usuário atual
			const allTickets = Array.isArray(response.data) ? response.data : [];
			const userTickets = allTickets.filter(ticket => ticket.user && ticket.user.id === user.id);

			// Separar por tipo (sessão x oficial) e status aberto
			const isOpenStatus = (t) => t?.status === "open" || t?.status === "pending";
			const sessionAssigned = userTickets.filter(t => t?.isOfficial === false && isOpenStatus(t));

			setSessionOpenTicketsCount(sessionAssigned.length);

			// Card "Atendimentos Abertos": voltar a contar como antes (total do usuário)
			setOpenTicketsCount(userTickets.length);
		} catch (error) {
			// Em caso de erro, definir como 0
			setOpenTicketsCount(0);
			setSessionOpenTicketsCount(0);
		}
	}, [user.id, advancedTicketsEnabled]);

	// Função para buscar tickets do Kanban (criador OU designado ao usuário)
	const fetchAssignedOpenTicketsAdvanced = useCallback(async () => {
		if (!advancedTicketsEnabled) { return; }
		try {
			// Obter filas do usuário (compatível com diferentes formatos)
			const queueIds = Array.isArray(user?.queues)
				? user.queues.map(q => (q?.UserQueue?.queueId ?? q?.id)).filter(Boolean)
				: [];

			const params = {};
			if (queueIds.length > 0) {
				params.queueIds = JSON.stringify(queueIds);
			}
			const { data } = await api.get("/ticket/kanban", { params });

			const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
			// Considerar apenas tickets "ativos" (o serviço já marca workflowState)
			const isActive = (t) => {
				const wf = t?.workflowState ?? "active";
				const st = t?.status;
				return (wf === "active" || wf == null) && (st === "open" || st === "pending" || st === "group");
			};
			const myActive = tickets.filter(isActive);
			setAssignedOpenTicketsCount(myActive.length);
			// Contagens de SLA com base no mesmo conjunto
			const warn = myActive.filter(t => t?.slaWarn && !t?.slaOverdue).length;
			const overdue = myActive.filter(t => t?.slaOverdue).length;
			setSlaWarnAssignedCount(warn);
			setSlaOverdueAssignedCount(overdue);
		} catch (error) {
			setAssignedOpenTicketsCount(0);
			setSlaWarnAssignedCount(0);
			setSlaOverdueAssignedCount(0);
		}
	}, [advancedTicketsEnabled, user?.queues]);

	// Atualiza data/hora a cada minuto
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentDateTime(new Date());
		}, 60000); // Atualiza a cada 1 minuto

		return () => clearInterval(timer);
	}, []);

	// Carrega dados do dashboard
	useEffect(() => {
		const loadDashboardData = async () => {
			
			setIsLoading(true);
			await Promise.all([
				fetchTasksStats(),
				fetchOpenTicketsCount(),
				fetchPendingAnnouncements(),
				advancedTicketsEnabled ? fetchAssignedOpenTicketsAdvanced() : Promise.resolve(),
			]);
			setIsLoading(false);
		};

		if (user?.companyId && user?.id) {
			loadDashboardData();
		}
	}, [user?.companyId, user?.id, fetchTasksStats, fetchOpenTicketsCount, fetchPendingAnnouncements, advancedTicketsEnabled, fetchAssignedOpenTicketsAdvanced]);

	// Atualização periódica da contagem de informativos pendentes (rede de segurança)
	useEffect(() => {
		if (!user?.id) return;

		// Atualizar a cada 30 segundos
		const interval = setInterval(() => {
			fetchPendingAnnouncements();
		}, 30000);

		return () => clearInterval(interval);
	}, [user?.id, fetchPendingAnnouncements]);

	// Escutar atualizações de informativos em tempo real
	useEffect(() => {
		if (!socket || !user?.companyId) return;

		// Escutar notificações gerais de informativos (como o popover faz)
		const handleAnnouncementNotification = (data) => {
			// Recalcular contagem para qualquer mudança em informativos
			if (data.action === "update" || data.action === "create" || data.action === "delete") {
				setTimeout(() => {
					fetchPendingAnnouncements();
				}, 1000); // Delay para garantir que o backend processou a mudança
			}
		};

		// Escutar eventos específicos de confirmação e dispensa
		const handleAnnouncementAcknowledged = (data) => {
			if (data.userId === user.id) {
				setTimeout(() => {
					fetchPendingAnnouncements();
				}, 500);
			}
		};

		const handleAnnouncementDismissed = (data) => {
			if (data.userId === user.id) {
				setTimeout(() => {
					fetchPendingAnnouncements();
				}, 500);
			}
		};

		socket.on("company-announcement-notification", handleAnnouncementNotification);
		socket.on("announcement-acknowledged", handleAnnouncementAcknowledged);
		socket.on("announcement-dismissed", handleAnnouncementDismissed);

		return () => {
			socket.off("company-announcement-notification", handleAnnouncementNotification);
			socket.off("announcement-acknowledged", handleAnnouncementAcknowledged);
			socket.off("announcement-dismissed", handleAnnouncementDismissed);
		};
	}, [socket, user?.companyId, fetchPendingAnnouncements]);

	// useEffect(() => {
	// 	if (ticketId && currentTicket.uuid === undefined) {
	// 		history.push("/tickets");
	// 	}
	// }, [ticketId, currentTicket.uuid, history]);

	const handleMouseDown = (e) => {
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};
	const handleSaveContact = async value => {
		if (value < 430)
			value = 430
		await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: value });
	}

	// Componente do Dashboard de Boas-vindas
	const WelcomeDashboard = () => {
		const dayNames = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
		const dayName = dayNames[currentDateTime.getDay()];
		const timeString = format(currentDateTime, 'HH:mm', { locale: ptBR });
		const displayName = settings?.displayAppName;

		// Função para verificar se deve mostrar aviso de vencimento
		const shouldShowExpirationWarning = () => {
			if (!user?.company?.dueDate || user?.profile !== "admin") {
				return false;
			}
			
			const today = new Date();
			const dueDate = parseISO(user.company.dueDate);
			const daysUntilExpiration = differenceInDays(dueDate, today);
			
			// Mostra apenas se estiver a 5 dias ou menos do vencimento (incluindo dias negativos para vencidos)
			return daysUntilExpiration <= 5;
		};

		return (
			<div className={classes.welcomeDashboard}>
				<div className={classes.dashboardHeader}>
					<div className={classes.logoContainer}>
						<img className={classes.logoImage} alt="Logo" />
					</div>
					<div className={classes.headerContent}>
						<Typography className={classes.systemName}>
							{displayName}
						</Typography>
					</div>
				</div>

				<div className={classes.welcomeSection}>
					<div className={classes.welcomeContent}>
						<div className={classes.welcomeText}>
							<Typography className={classes.greeting}>
								Bem-vindo, {user?.name}.
							</Typography>
							<Typography className={classes.datetime}>
								Hoje é {dayName}, {timeString}.
							</Typography>
						</div>
						<Avatar
							className={classes.avatar2}
							src={profileUrl}
						/>
					</div>
				</div>

				{shouldShowExpirationWarning() && (
					<div className={classes.expirationSection}>
						<div className={classes.expirationCard}>
							<ErrorOutline className={classes.expirationIcon} />
							<Typography className={classes.expirationText}>
								Expiração do plano: {dateToClient(user.company.dueDate)}
							</Typography>
						</div>
					</div>
				)}

				{pendingAnnouncementsCount > 0 && (
					<div className={classes.expirationSection}>
						<div className={classes.pendingAnnouncementsCard}>
							<Announcement className={classes.expirationIcon} />
							<Typography className={classes.expirationText}>
								Verifique seus informativos, você tem {pendingAnnouncementsCount} confirmação{pendingAnnouncementsCount > 1 ? 'ões' : ''} de leitura pendente{pendingAnnouncementsCount > 1 ? 's' : ''}.
							</Typography>
						</div>
					</div>
				)}

				<Typography className={classes.summaryText}>
					Resumo dos seus atendimentos e tarefas do dia.
				</Typography>

				{isLoading ? (
					<Box display="flex" justifyContent="center" alignItems="center" flex={1}>
						<CircularProgress size={40} />
					</Box>
				) : (
					<Grid container spacing={1} className={classes.statsGrid}>
						<Grid item xs={12} sm={6} md={3} lg={3}>
							<Card className={`${classes.statCard} ${classes.overdueCard}`}>
								<CardContent className={classes.statCardContent}>
									<div className={classes.statHeader}>
										<Typography className={classes.statLabel}>
											Tarefas Atrasadas
										</Typography>
									</div>
									<div className={classes.statFooter}>
										<Warning className={classes.statIcon} />
										<Typography className={classes.statNumber}>
											{tasksStats.overdue}
										</Typography>
									</div>
								</CardContent>
							</Card>
						</Grid>
						
						<Grid item xs={12} sm={6} md={3} lg={3}>
							<Card className={`${classes.statCard} ${classes.todayCard}`}>
								<CardContent className={classes.statCardContent}>
									<div className={classes.statHeader}>
										<Typography className={classes.statLabel}>
											Tarefas de Hoje
										</Typography>
									</div>
									<div className={classes.statFooter}>
										<Today className={classes.statIcon} />
										<Typography className={classes.statNumber}>
											{tasksStats.today}
										</Typography>
									</div>
								</CardContent>
							</Card>
						</Grid>
						
						<Grid item xs={12} sm={6} md={3} lg={3}>
							<Card className={`${classes.statCard} ${classes.tomorrowCard}`}>
								<CardContent className={classes.statCardContent}>
									<div className={classes.statHeader}>
										<Typography className={classes.statLabel}>
											Tarefas de Amanhã
										</Typography>
									</div>
									<div className={classes.statFooter}>
										<Schedule className={classes.statIcon} />
										<Typography className={classes.statNumber}>
											{tasksStats.tomorrow}
										</Typography>
									</div>
								</CardContent>
							</Card>
						</Grid>
						
						<Grid item xs={12} sm={6} md={3} lg={3}>
							<Card className={`${classes.statCard} ${classes.ticketsCard}`}>
								<CardContent className={classes.statCardContent}>
									<div className={classes.statHeader}>
										<Typography className={classes.statLabel}>
											Atendimentos Abertos
										</Typography>
									</div>
									<div className={classes.statFooter}>
										<Chat className={classes.statIcon} />
										<Typography className={classes.statNumber}>
											{openTicketsCount}
										</Typography>
									</div>
								</CardContent>
							</Card>
						</Grid>

						{advancedTicketsEnabled && (
							<Grid item xs={12} sm={6} md={3} lg={3}>
								<Card className={`${classes.statCard} ${classes.ticketsCard}`}>
									<CardContent className={classes.statCardContent}>
										<div className={classes.statHeader}>
											<Typography className={classes.statLabel}>
												Meus Tickets Abertos
											</Typography>
										</div>
										<div className={classes.statFooter}>
											<AssignmentInd className={classes.statIcon} />
											<Typography className={classes.statNumber}>
												{assignedOpenTicketsCount}
											</Typography>
										</div>
									</CardContent>
								</Card>
							</Grid>
						)}

						{advancedTicketsEnabled && (
							<>
								<Grid item xs={12} sm={6} md={3} lg={3}>
									<Card className={`${classes.statCard} ${classes.slaWarnCard}`}>
										<CardContent className={classes.statCardContent}>
											<div className={classes.statHeader}>
												<Typography className={classes.statLabel}>
													SLA Vencendo
												</Typography>
											</div>
											<div className={classes.statFooter}>
												<Warning className={classes.statIcon} />
												<Typography className={classes.statNumber}>
													{slaWarnAssignedCount}
												</Typography>
											</div>
										</CardContent>
									</Card>
								</Grid>

								<Grid item xs={12} sm={6} md={3} lg={3}>
									<Card className={`${classes.statCard} ${classes.slaOverdueCard}`}>
										<CardContent className={classes.statCardContent}>
											<div className={classes.statHeader}>
												<Typography className={classes.statLabel}>
													SLA Vencidos
												</Typography>
											</div>
											<div className={classes.statFooter}>
												<ErrorOutline className={classes.statIcon} />
												<Typography className={classes.statNumber}>
													{slaOverdueAssignedCount}
												</Typography>
											</div>
										</CardContent>
									</Card>
								</Grid>
							</>
						)}
					</Grid>
				)}
			</div>
		);
	};
	const handleMouseMove = useCallback(
		(e) => {
			const newWidth = e.clientX - document.body.offsetLeft;
			if (
				newWidth > minTicketsManagerWidth &&
				newWidth < maxTicketsManagerWidth
			) {
				ticketsManagerWidthRef.current = newWidth;
				setTicketsManagerWidth(newWidth);
			}
		},
		[]
	);

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	return (
		<QueueSelectedProvider>
			<div className={classes.chatContainer}>
				<div className={classes.chatPapper}>
					<div
						className={classes.contactsWrapper}
						style={{ width: ticketsManagerWidth }}
					>
						<TicketsManager />
						<div onMouseDown={e => handleMouseDown(e)} className={classes.dragger} />
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<>
								{/* <Suspense fallback={<CircularProgress />}> */}
								<Ticket />
								{/* </Suspense> */}
							</>
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square variant="outlined" className={classes.welcomeMsg}>
									<WelcomeDashboard />
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;